/*
 * Calendar feed links, on the profile page (ROADMAP §7).
 *
 * Its own component rather than more lines in ProfilePage.js, which is already 28KB —
 * the same call that was made for TwoFactorSetup.jsx.
 *
 * ⚠️ Two pieces of copy in here are load-bearing and should not be trimmed as waffle:
 *
 *  1. **The delay warning.** Google re-reads a subscribed calendar on its own schedule,
 *     which can be a day. Without a warning, the first thing anybody does is book leave,
 *     refresh Google, see nothing and report the feature as broken. It is the single
 *     most likely support ticket this feature generates and the only defence is saying
 *     so before it happens.
 *  2. **"Treat it like a password."** The link works without signing in, by design,
 *     because a calendar application cannot sign in. Anyone holding it sees the leave.
 */
import React, { useState, useEffect } from 'react';
import { Copy, Check, RefreshCw, Loader2, Clock } from 'lucide-react';
import { Button } from './ui/button';
import { profileApi } from '../lib/api';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const CalendarFeedCard = () => {
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [feed, setFeed] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(null);
  const [checking, setChecking] = useState(false);

  /*
   * Re-read the status. ⚠️ Errors are shown rather than swallowed: a button that appears
   * to do nothing is indistinguishable from a feed nothing has fetched, which is the
   * exact confusion this whole panel exists to remove.
   */
  const reload = async () => {
    setChecking(true);
    try {
      const { data } = await profileApi.getCalendarFeed();
      setFeed(data);
    } catch (error) {
      toast.error('Couldn’t check just now. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await profileApi.getCalendarFeed();
        setFeed(data);
      } catch (error) {
        // ⚠️ Say it failed rather than rendering an empty card. An empty card reads as
        // "this company has it switched off", which is a different and wrong answer.
        setFailed(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const copy = async (url, which) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(which);
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      // Clipboard access is refused on an insecure origin and in some locked-down
      // browsers. Failing silently would look like a dead button.
      toast.error('Couldn’t copy. Select the link and copy it by hand.');
    }
  };

  const reset = async () => {
    setResetting(true);
    try {
      const { data } = await profileApi.resetCalendarFeed();
      setFeed(data);
      setConfirming(false);
      toast.success('New links created. The old ones have stopped working.');
    } catch (error) {
      toast.error('Couldn’t create new links. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" />
        Loading your calendar links...
      </div>
    );
  }

  if (failed) {
    return (
      <p className="text-sm text-muted-foreground">
        Couldn&rsquo;t load your calendar links. Please refresh the page.
      </p>
    );
  }

  if (!feed?.enabled) {
    return (
      <p className="text-sm text-muted-foreground">
        Your company has switched calendar feeds off.
      </p>
    );
  }

  /*
   * ⚠️ THE `webcal://` "ADD" BUTTON WAS REMOVED ON 20/08/2026, ROB'S CALL, AND IT SHOULD
   * NOT COME BACK WITHOUT NEW EVIDENCE. It handed the link to whatever the operating
   * system had registered for the scheme, which for him was Outlook — the one client on
   * his machine that cannot accept a subscription at all. So the most prominent control
   * on the panel was the one guaranteed to fail, and it failed with Microsoft's wording
   * rather than ours, which made it look like OUR bug twice in one afternoon.
   *
   * Copy works everywhere: Google's web interface wants a pasted https URL and rejects
   * webcal outright, Apple accepts a paste, and Outlook's own dialog takes one too.
   * ⚠️ What is genuinely lost is the one-click path for Apple users. Worth revisiting if
   * somebody asks — but as a second, clearly-labelled option, never as the primary.
   */

  /*
   * ⚠️ THE WHOLE POINT OF THIS LINE. A subscription is fetched on the calendar
   * provider's schedule, and there is no way from inside our product to tell "Google has
   * not looked yet" from "HolidayHive is serving the wrong thing". Rob hit exactly that
   * on 19/08/2026 and it was unanswerable without fetching the feed by hand. Showing
   * when it was last read turns it into a glance.
   *
   * Three states:
   *   unseen — nothing has fetched it SINCE WE STARTED COUNTING.
   *   recent — working. Nothing to do.
   *   stale  — the calendar app has stopped looking. Ours is fine; theirs is behind.
   *
   * ⚠️ **"UNSEEN" IS NOT "NOT SUBSCRIBED", AND SAYING SO WAS A REAL BUG.** The first
   * wording here read "Not added to a calendar yet", which asserts something this data
   * cannot know. Rob caught it on 20/08/2026: his Google subscription was live and
   * correct, and the card told him he had never added it — because Google had not
   * happened to poll since the counting was deployed. ⚠️ **Every existing subscriber
   * would have been told the same thing on the day this shipped**, which is worse than
   * not building the feature: it invites people to "fix" a subscription that works.
   *
   * The copy now describes what we actually observed — that nothing has checked in yet —
   * and names the innocent explanation, so somebody who HAS subscribed is reassured
   * instead of contradicted.
   */
  const lastChecked = (feed) => {
    const checks = feed?.checks || [];
    if (checks.length === 0) {
      return (
        <span className="text-xs text-muted-foreground">
          Waiting for a calendar app to check in &mdash; if you&rsquo;ve already added
          this, it&rsquo;ll show here after the next check
        </span>
      );
    }
    /*
     * ⚠️ ONE LINE PER CALENDAR APP. The same link is commonly added to two or three, and
     * a single line made them overwrite each other — the name flipping between apps,
     * reading as one erratic client rather than three healthy ones. Rob spotted the flaw
     * as a question before it shipped: "will each new one write over the last?"
     */
    return (
      <div className="space-y-0.5">
        {checks.map((c) => {
          const when = new Date(c.last_checked_at);
          const stale = Date.now() - when.getTime() > 24 * 60 * 60 * 1000;
          return (
            <div
              key={c.client}
              className={`text-xs ${stale ? 'text-amber-600' : 'text-muted-foreground'}`}
            >
              {c.client} last checked {formatDistanceToNow(when, { addSuffix: true })}
              {stale && ' — longer than usual'}
            </div>
          );
        })}
      </div>
    );
  };

  const feedRow = (label, hint, feed, which) => {
    const url = feed.url;
    return (
    <div className="py-4 border-t border-border">
      <div className="font-medium text-sm">{label}</div>
      <p className="text-xs text-muted-foreground mb-2">{hint}</p>
      <div className="flex items-center gap-2">
        <code
          className="flex-1 min-w-0 truncate text-xs font-mono bg-muted/40 border border-border rounded-md px-3 py-2 text-muted-foreground"
          data-testid={`calendar-feed-url-${which}`}
          title={url}
        >
          {url}
        </code>
        <Button variant="outline" size="sm" onClick={() => copy(url, which)}>
          {copied === which
            ? <><Check className="w-4 h-4 mr-1" />Copied</>
            : <><Copy className="w-4 h-4 mr-1" />Copy</>}
        </Button>
      </div>
      <div className="mt-2 flex items-start gap-2">
        <div className="flex-1 min-w-0">{lastChecked(feed)}</div>
        {/*
          ⚠️ "Check again" re-reads OUR RECORD of who has fetched the feed. It cannot make
          Google go and look — nothing can, that is the provider's decision — and the
          label must not imply otherwise. A button called "Refresh" next to a calendar
          would be read as "update my calendar now", and a control that appears to fail
          every time is worse than no control. One click updates all three rows, since
          they come from a single call.
        */}
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 shrink-0"
          onClick={reload}
          disabled={checking}
          title="Re-read who has checked these feeds. This can't make your calendar app look sooner."
          data-testid={`calendar-feed-recheck-${which}`}
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${checking ? 'animate-spin' : ''}`} />
          Check again
        </Button>
      </div>
    </div>
    );
  };

  return (
    <div data-testid="calendar-feed-card">
      <p className="text-sm text-muted-foreground mb-2">
        Add your leave to Google Calendar, Outlook or Apple Calendar. These links are
        private to you &mdash; anyone who has one can see what they show, so treat them
        like a password.
      </p>

      {/* ⚠️ "the only one that includes you" is doing real work, not padding. The two
          who's-off feeds deliberately exclude the subscriber so that adding all three
          does not draw the same holiday three times (Rob, 20/08/2026). Somebody who adds
          only a who's-off feed needs to know where their own leave went. */}
      {feedRow(
        'My leave',
        'Your own booked and requested leave — the only one of the three that includes you. Requests still waiting show as tentative.',
        feed.my_leave,
        'me',
      )}

      {/* ⚠️ Department before company, deliberately. It is the narrower and more useful
          of the two for most people, and a list puts the emphasis on what comes first. */}
      {feed.department ? feedRow(
        feed.department_name ? `${feed.department_name} — who’s off` : 'My department',
        'Everyone else in your department. Won’t mark you as busy.',
        feed.department,
        'department',
      ) : feed.department_restricted ? (
        /*
         * ⚠️ **A DIFFERENT ABSENCE WITH A DIFFERENT REASON, AND THEY MUST NOT SHARE A
         * SENTENCE.** The branch below says "you're not in a department yet" — which is a
         * confident wrong answer to somebody who IS in one and has had this withheld by
         * company policy. The backend sends the two cases separately for exactly this.
         *
         * ⚠️ And it says what the COMPANY does, never what the person is. "You are only an
         * employee" is not a sentence anybody should read about themselves, and the fact
         * that matters is true whoever is asking.
         */
        <div className="py-4 border-t border-border" data-testid="department-feed-restricted">
          <div className="font-medium text-sm text-muted-foreground">My department</div>
          <p className="text-xs text-muted-foreground">
            Your company doesn&rsquo;t share colleagues&rsquo; leave by calendar link.
          </p>
        </div>
      ) : (
        /*
         * ⚠️ SAY WHY IT IS MISSING RATHER THAN JUST OMITTING IT. The row used to vanish
         * silently for anyone with no department, and the first person to look at it —
         * Rob, 19/08/2026 — asked "am I missing something?". A customer would ask the
         * same and have nobody to ask. An absence with no explanation cannot be
         * diagnosed by the person looking at it.
         *
         * Still no dead link: offering a URL that is guaranteed to return an empty
         * calendar would be a different and worse kind of confusing.
         */
        <div className="py-4 border-t border-border">
          <div className="font-medium text-sm text-muted-foreground">My department</div>
          <p className="text-xs text-muted-foreground">
            You&rsquo;re not in a department yet, so there&rsquo;s no department feed to
            add. Once someone puts you in one, a third link will appear here.
          </p>
          {/* ⚠️ THIS one may safely say "you have not" — unlike the last-checked line
              above, the department is a fact we hold, not an absence of evidence. */}
        </div>
      )}

      {/*
        ⚠️ THE TWO WHO'S-OFF FEEDS ARE A CHOICE, NOT A PAIR, AND THE UI MUST SAY SO.
        Rob added both on 20/08/2026 and saw every Engineering colleague twice — Sundar
        Pichai drew two bars on the same day. That is not a bug and cannot be fixed by
        filtering: a department is a SUBSET of a company, so any feed containing everyone
        necessarily contains everyone in your department.
        (Excluding the subscriber themselves worked because that is exactly one person.
        There is no equivalent trick here, and "everyone except my department" would be a
        feed called Everyone that does not contain everyone.)
        So the fix is honesty about the relationship. Only shown when they HAVE a
        department feed — with no department there is no overlap and the warning would be
        a puzzle rather than a help.
      */}
      {feed.company_restricted ? (
        /*
         * ⚠️ Same wording as the department case above, on purpose. A person restricted
         * from both should read one consistent fact about their company rather than two
         * differently-worded refusals that invite them to wonder which is the real one.
         */
        <div className="py-4 border-t border-border" data-testid="company-feed-restricted">
          <div className="font-medium text-sm text-muted-foreground">Everyone — who’s off</div>
          <p className="text-xs text-muted-foreground">
            Your company doesn&rsquo;t share colleagues&rsquo; leave by calendar link.
          </p>
        </div>
      ) : feedRow(
        'Everyone — who’s off',
        feed.department
          ? 'Everyone else in the company, as you see it on the wallchart. Includes your department, so add this one or the one above — adding both shows your colleagues twice.'
          : 'Everyone else in the company, as you see it on the wallchart. Won’t mark you as busy.',
        feed.whos_off,
        'team',
      )}

      <div className="py-4 border-t border-border">
        {confirming ? (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-sm text-muted-foreground">
              Any calendar already using these links will stop updating. Sure?
            </span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                onClick={reset}
                disabled={resetting}
                data-testid="calendar-feed-reset-confirm"
              >
                {resetting
                  ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" />Working...</>
                  : 'Yes, create new links'}
              </Button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <span className="text-xs text-muted-foreground">
              Shared one by mistake? Creating new links stops the old ones working.
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setConfirming(true)}
              data-testid="calendar-feed-reset"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Reset links
            </Button>
          </div>
        )}
      </div>

      {/*
        ⚠️ "Up to a day" was optimistic and has been corrected. Timetastic, who have run
        this for years, publish a status page for it and report delays of two to three
        days. Understating it means the first slow refresh reads as a bug in our app,
        which is precisely the support ticket this paragraph exists to prevent.
      */}
      <div className="mt-2 p-4 rounded-lg bg-amber-500/10 border border-amber-500/20">
        <p className="text-sm text-muted-foreground flex items-start gap-2">
          <Clock className="w-4 h-4 mt-0.5 shrink-0 text-amber-600" />
          <span>
            Your calendar app decides for itself how often to check &mdash; Google can take
            a day, occasionally two or three. Apple and Outlook are usually quicker. The
            line under each link above tells you when yours last looked, so you can see
            the difference between &ldquo;nothing has changed&rdquo; and &ldquo;nothing has
            checked&rdquo;.
            <br /><br />
            If one is stuck on old information, try removing the calendar and adding it
            again. ⚠️ If that doesn&rsquo;t help, it&rsquo;s because Google remembers what
            it fetched from a given address &mdash; use <strong>Reset links</strong> above
            to get brand-new addresses it has never seen, then add those. If a link never
            works at all, your company&rsquo;s firewall may be blocking it.
          </span>
        </p>
      </div>
    </div>
  );
};

export default CalendarFeedCard;
